/**
 * `trust_screen` renderer — composes `@nextday/funnel-ui` `TrustScreen`.
 * Pure presentation; the orchestrator's global ContinueButton advances.
 *
 * Step JSON shape (plan §5 step 9 — trust beat before email/account):
 *
 *   {
 *     "type": "trust_screen",
 *     "data": {
 *       "headline": "We take your privacy seriously.",
 *       "description": "Quick reassurance before you create an account.",
 *       "privacyItems": [
 *         { "icon": "gravity-ui:lock", "text": "Conversations stay private." },
 *         { "icon": "gravity-ui:eye-slash", "text": "Nothing is shared." }
 *       ],
 *       "testimonials": [
 *         { "quote": "Felt like a real connection.", "author": "Riley" }
 *       ]
 *     }
 *   }
 */
import { TrustScreen } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import { testimonialSchema } from "@survey-kit/renderers/testimonialSchema";
import type { RendererProps } from "@survey-kit/types";

const trustItemSchema = z.object({
  icon: z.string().min(1),
  text: z.string().min(1),
});

export const trustScreenSchema = z
  .object({
    headline: z.string().min(1),
    description: z.string().optional(),
    privacyItems: z.array(trustItemSchema).optional(),
    testimonials: z.array(testimonialSchema).optional(),
  })
  .strict();

export type TrustScreenData = z.infer<typeof trustScreenSchema>;

const TrustScreenRenderer = ({ data, step }: RendererProps<TrustScreenData>) => (
  <TrustScreen
    headline={data.headline}
    description={data.description}
    privacyItems={data.privacyItems}
    testimonials={data.testimonials}
    testId={`renderer-trust-screen-${step.id}`}
  />
);

export const trustScreenRenderer = createRenderer({
  type: "trust_screen",
  dataSchema: trustScreenSchema,
  label: "Trust screen",
  Component: TrustScreenRenderer,
});

export default trustScreenRenderer;
